
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UseRsvpUserDataResult {
  selectedDates: string[] | null;
  selectedTimes: string[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useRsvpUserData = (
  eventId: string,
  enabled: boolean = true
): UseRsvpUserDataResult => {
  const [selectedDates, setSelectedDates] = useState<string[] | null>(null);
  const [selectedTimes, setSelectedTimes] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!eventId || !enabled) return;

    const fetchUserRsvpData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          return;
        }
        
        // Try to find the user's invite by their user_id
        let { data: invite, error: inviteError } = await supabase
          .from('event_invites')
          .select('selected_dates, selected_times')
          .eq('event_id', eventId)
          .eq('claimed_by_user_id', user.id)
          .maybeSingle();
          
        if (inviteError) {
          throw inviteError;
        }
        
        // If not found by user_id, try to find by phone number
        if (!invite) {
          // Get user's phone number
          const { data: userProfile, error: userError } = await supabase
            .from('users')
            .select('phone_number')
            .eq('user_id', user.id)
            .maybeSingle();
            
          if (userError) {
            throw userError;
          }
          
          if (userProfile?.phone_number) {
            // Try to find invite by phone
            const { data: phoneInvite, error: phoneInviteError } = await supabase
              .from('event_invites')
              .select('selected_dates, selected_times')
              .eq('event_id', eventId)
              .eq('phone_number', userProfile.phone_number)
              .maybeSingle();
              
            if (phoneInviteError) {
              throw phoneInviteError;
            }
            
            invite = phoneInvite;
          }
        }
        
        if (invite) {
          setSelectedDates(invite.selected_dates);
          setSelectedTimes(invite.selected_times);
        }
      } catch (err) {
        console.error('Error fetching RSVP data:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch RSVP data'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserRsvpData();
  }, [eventId, enabled]);

  return { selectedDates, selectedTimes, isLoading, error };
};
